import type { InboxItem } from '@secretario/shared';
import { useEffect, useRef } from 'react';
import {
  INBOX_CONFIG_EVENT,
  inboxActive,
  inboxSeenAt,
  inboxSupported,
  listInbox,
  markInboxSeen,
  watchInbox,
} from '../../lib/inbox.js';
import {
  INBOX_OPEN_EVENT,
  addInbox,
  inboxConversation,
  keepOnlyInbox,
  pendingInbox,
} from '../../lib/inboxQueue.js';
import { useStage } from './StageProvider.js';

/** O personagem reage quando chega mensagem (o Palco ouve este evento). */
export const INBOX_EVENT = 'secretario:inbox';

const cardId = (key: string) => `inbox:${key}`;

/**
 * Mensagens de outros apps no palco, um cartão por vez:
 * - falas novas da mesma conversa entram no cartão aberto, sem apagar o que a pessoa
 *   está escrevendo;
 * - mensagem de outra conversa não troca o cartão: entra na fila ("+2 conversas") e
 *   aparece em "Próxima" ou na etiqueta da Início;
 * - o cartão não sai sozinho; "Depois" fecha e a conversa continua esperando na etiqueta.
 *
 * O ouvinte nativo fica ligado desde a abertura (é barato e só entrega quando o
 * Android já permitiu); a decisão de mostrar é tomada a cada mensagem, para a chave
 * ligada em Conta › Notificações valer na hora, sem reabrir o app.
 */
export function InboxWatcher() {
  const stage = useStage();
  const stageRef = useRef(stage);
  stageRef.current = stage;
  // A conversa que está no cartão agora (null quando nenhum cartão de mensagem está aberto).
  const current = useRef<string | null>(null);

  // O cartão saiu (Depois, voltar, Esc, respondida): ninguém está aberto.
  const cards = stage.state.cards;
  useEffect(() => {
    if (current.current && !cards.some((view) => view.id === cardId(current.current!))) {
      current.current = null;
    }
  }, [cards]);

  useEffect(() => {
    let alive = true;
    let stop = () => {};
    // A chave é lida de novo a cada mensagem, visita ou mudança em Conta › Notificações.
    let active = false;
    const refresh = async () => {
      active = await inboxActive();
      return active && alive;
    };

    // Abre (ou atualiza) o cartão de uma conversa, com quantas outras esperam atrás.
    const present = (key: string) => {
      const conversation = inboxConversation(key);
      if (!conversation) return;
      const others = pendingInbox().filter((item) => item.key !== key);
      current.current = key;
      markInboxSeen(Math.max(inboxSeenAt(), conversation.lines.at(-1)?.at ?? conversation.item.at));
      stageRef.current.inbox(conversation.item, {
        lines: conversation.lines,
        more: others.length,
        onNext: others.length > 0 ? next : undefined,
      });
    };
    // A próxima conversa que espera (a mais nova), depois de responder, descartar ou pular.
    function next() {
      const waiting = pendingInbox().filter((item) => item.key !== current.current);
      const target = waiting[0];
      if (target) present(target.key);
    }

    const take = (item: InboxItem) => {
      if (item.mine) {
        // Respondeu pelo aviso do próprio WhatsApp: o cartão dessa conversa fecha.
        addInbox(item);
        if (current.current === item.key) stageRef.current.close(cardId(item.key));
        return;
      }
      if (!addInbox(item)) return;
      window.dispatchEvent(new CustomEvent(INBOX_EVENT));
      const open = current.current;
      if (!open || open === item.key) present(item.key);
      // Outra conversa: o cartão aberto só ganha o "+N" (não troca embaixo de quem escreve).
      else present(open);
    };

    const catchUp = async () => {
      if (!(await refresh())) return;
      const since = inboxSeenAt();
      const bar = await listInbox();
      if (!alive) return;
      // O que saiu da barra do Android (lido no próprio app) sai daqui também.
      keepOnlyInbox(new Set(bar.map((item) => item.key)));
      let newest: InboxItem | null = null;
      for (const item of [...bar].reverse()) {
        if (addInbox(item) && item.at > since && (!newest || item.at > newest.at)) newest = item;
      }
      if (newest && !current.current) present(newest.key);
      else if (current.current) present(current.current);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void catchUp();
    };
    const onConfig = () => {
      void refresh().then((on) => {
        // Ligou agora: o que já está na barra desde a última vez conta como novo.
        if (on) void catchUp();
      });
    };
    const onOpen = () => {
      const target = pendingInbox()[0];
      if (target) present(target.key);
    };
    if (inboxSupported()) {
      void (async () => {
        stop = await watchInbox((item) => {
          if (!alive || document.visibilityState !== 'visible') return;
          if (active) {
            take(item);
            return;
          }
          // Chave desligada na memória? Confere de novo (pode ter sido ligada agora).
          void refresh().then((on) => {
            if (on) take(item);
          });
        });
        await catchUp();
      })();
    }
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener(INBOX_CONFIG_EVENT, onConfig);
    window.addEventListener(INBOX_OPEN_EVENT, onOpen);
    // Só no desenvolvimento: uma notificação de mentira para ver o cartão no navegador.
    const devInbox = (event: Event) => {
      const item = (event as CustomEvent<InboxItem>).detail;
      if (import.meta.env.DEV && item) take(item);
    };
    if (import.meta.env.DEV) window.addEventListener('secretario:dev-inbox', devInbox);
    return () => {
      alive = false;
      stop();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(INBOX_CONFIG_EVENT, onConfig);
      window.removeEventListener(INBOX_OPEN_EVENT, onOpen);
      if (import.meta.env.DEV) window.removeEventListener('secretario:dev-inbox', devInbox);
    };
  }, []);
  return null;
}
